import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import Button from '../Button';
import Field from '../Field';
import FakeLabel from '../FakeLabel';
import { useQuery } from '@tanstack/react-query';
import agentService from '../../services/agentService';
import styled from 'styled-components';
import teamService from '../../services/teamService';
import ChatPanel from './ChatPanel';

const StyledNodeSettings = styled.div`
  width: 24rem;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: var(--card);
  color: var(--ink);
  box-shadow: var(--shadow-lg);

  .panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px;
    border-bottom: 1px solid var(--line);

    h3 {
      margin: 0;
      font-size: 16px;
      line-height: 22px;
      font-weight: 700;
      color: var(--ink);
    }
  }

  .panel-body {
    flex: 1;
    padding: 16px;
  }

  .panel-foot {
    padding: 16px;
    border-top: 1px solid var(--line);
  }

  .flex-wrap {
    display: flex;
    align-items: center;
    gap: 10px;
    position: relative;

    .description {
      display: none;
      position: absolute;
      left: 40%;
      z-index: 5;
      padding: 5px;
      border-radius: 5px;
      border: 1px solid var(--line);
      background-color: var(--card);
      color: var(--ink);
      box-shadow: var(--shadow);
    }

    &:hover {
      .description {
        display: block;
      }
    }

    &.tag-section {
      justify-content: space-between;

      .tag-text {
        font-size: 12px;
        line-height: 16px;
        text-align: center;
        padding: 5px 10px;
        border-radius: 10px;
        border: 1px solid var(--line);
        background: var(--card-warm);
        color: var(--quiet);
        text-transform: capitalize;
      }
    }
  }

  .react-select__multi-value__label {
    .tag-text {
      display: none !important;
    }
  }
`;

export const NodeSettingsPanel = ({ node, onUpdateNode, onClose }) => {
  const [label, setLabel] = useState(node.data.label || '');
  const [instructions, setInstructions] = useState(node.data.instructions || '');
  const [model, setModel] = useState(node.data.model || '');
  const [condition, setCondition] = useState(node.data.condition || '');
  const [agentId, setAgentId] = useState();
  const [teamId, setTeamId] = useState();
  const [modelsKeys, setModelsKeys] = useState([]);
  const [activeModelsKeys, setActiveModelsKeys] = useState('OpenAi');
  const [models, setModels] = useState([]);
  const [allModel, setAllModel] = useState([]);
  const [prompt, setPrompt] = useState('');

  const get_all_models = useQuery({
    queryKey: ['get-all-models'],
    queryFn: () => agentService.getAllModels(),
    staleTime: Infinity,
  });

  const getAllAgentData = useQuery({
    queryKey: ['get-all-agents'],
    queryFn: () => agentService.getAllAgent(),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const getAllTeamData = useQuery({
    queryKey: ['get-all-teams'],
    queryFn: () => teamService.getAllTeam(),
    enabled: false,
  });

  const getModels = async () => {
    const allModels = get_all_models?.data?.models?.flatMap(providerObj => {
      const providerName = Object.keys(providerObj)[0];
      const models = providerObj[providerName];
      return models.map(model => ({
        label: model?.name,
        value: model?.id,
      }));
    });
    const convertedData = get_all_models?.data?.models?.map(providerObj => {
      const providerName = Object.keys(providerObj)[0];
      const models = providerObj[providerName];

      const formattedModels = models.map(model => ({
        label: (
          <div className="flex-wrap">
            <img src={model?.icon} alt="img" width={30} height={30} />
            <span className="text">{model?.name}</span>
            <span className="description">{model?.description}</span>
          </div>
        ),
        value: model?.id,
        description: model?.description,
      }));

      // Return in the same structure
      return {
        [providerName]: formattedModels,
      };
    });

    const providerKeys = get_all_models?.data?.models?.map(providerObj => {
      const providerName = Object.keys(providerObj)[0];
      return {
        label: providerName,
        value: providerName,
      };
    });

    setModels(convertedData);
    setModelsKeys(providerKeys);
    setAllModel(allModels);

    // setActiveModelsKeys(providerKeys?.find(elem => elem?.value == findProvider(details?.model_id)));
  };

  const formatAgents = agents => {
    return agents?.map(agent => ({
      label: agent.agent_name,
      value: agent.id || agent.agent_id,
    }));
  };

  const formatTeams = teams => {
    return teams?.map(team => ({
      label: team.team_name,
      value: team.team_id,
    }));
  };

  const findProvider = modelId => {
    const providerObj = models.find(obj => {
      const key = Object.keys(obj)[0];
      return obj[key].some(model => model.value === modelId);
    });

    return providerObj ? Object.keys(providerObj)[0] : null;
  };

  useEffect(() => {
    if (node.data.type === 'llm') {
      getModels();
    }
  }, [get_all_models.isSuccess, node.data.type === 'llm']);

  useEffect(() => {
    setLabel(node.data?.label || '');
    setInstructions(node.data?.instructions || node.data?.step_description || '');
    setPrompt(node.data?.prompt || '');
    setModel(node.data?.model || '');
    setCondition(node.data?.condition || '');
    if (node.data?.agentId || node.data?.agent_id) {
      const agentValue =
        typeof node.data?.agentId === 'object'
          ? node.data.agentId
          : formatAgents(getAllAgentData?.data?.agents)?.find(
              e => e?.value === (node.data?.agent_id || node.data?.agentId),
            );
      setAgentId(agentValue || node.data?.agentId || '');
    } else {
      setAgentId(undefined);
    }
    if (node.data?.teamId || node.data?.team_id) {
      const teamValue =
        typeof node.data?.teamId === 'object'
          ? node.data.teamId
          : formatTeams(getAllTeamData?.data?.teams)?.find(
              e => e?.value === (node.data?.team_id || node.data?.teamId),
            );
      setTeamId(teamValue || node.data?.teamId || '');
    } else {
      setTeamId(undefined);
    }
  }, [node, modelsKeys]);

  useEffect(() => {
    if (node.data?.model) {
      setActiveModelsKeys(modelsKeys?.find(elem => elem?.value == findProvider(node.data?.model)));
      setModel(allModel?.find(e => e?.value === node.data?.model) || '');
    }
  }, [node, modelsKeys, allModel]);

  const handleSave = async () => {
    await onUpdateNode(node.id, {
      label,
      instructions,
      model,
      condition,
      agentId,
      teamId,
      prompt,
      provider: activeModelsKeys,
    });
    onClose();
  };

  useEffect(() => {
    if (!getAllTeamData?.data?.teams) {
      getAllTeamData.refetch();
    }
  }, [getAllTeamData]);

  useEffect(() => {
    if (!get_all_models?.data?.models) {
      get_all_models.refetch();
    }
  }, [get_all_models]);

  return (
    <StyledNodeSettings style={{ maxHeight: 'calc(100dvh - 130px)' }}>
      <div className="panel-head">
        <h3>{node.data.type === 'chat' ? 'Chat' : 'Node Settings'}</h3>
        <Button variant="cross" onClick={onClose} width="20px">
          <X className="h-4 w-4" />
        </Button>
      </div>
      {node.data.type === 'chat' ? (
        <ChatPanel />
      ) : (
        <>
          <div className="flex-1">
            <div className="panel-body space-y-4">
              <div className="space-y-2">
                <FakeLabel htmlFor="node-type">Type</FakeLabel>
                <Field type="text" id="node-type" value={node.data.type || ''} disabled className="bg-muted" />
              </div>

              <div className="space-y-2">
                <FakeLabel htmlFor="node-label">Step Name</FakeLabel>
                <Field
                  type="text"
                  id="node-label"
                  value={label}
                  onChange={e => setLabel(e.target.value)}
                  placeholder="Node label..."
                />
              </div>

              {node.data.type === 'agent' && (
                <>
                  <div className="space-y-2">
                    <FakeLabel htmlFor="agent_id">Select Agent</FakeLabel>
                    <Field
                      options={formatAgents(getAllAgentData?.data?.agents)}
                      type="select"
                      id="agent_id"
                      value={agentId}
                      onChange={e => setAgentId(e.target.value)}
                      placeholder="Select any agent"
                    />
                  </div>
                  <div className="space-y-2">
                    <FakeLabel htmlFor="node_instructions">Instructions</FakeLabel>
                    <Field
                      type="textarea"
                      id="node_instructions"
                      value={instructions}
                      onChange={e => setInstructions(e.target.value)}
                      placeholder="Agent instructions..."
                      rows={6}
                    />
                  </div>
                </>
              )}

              {node.data.type === 'llm' && (
                <>
                  <div className="space-y-2">
                    <FakeLabel htmlFor="node_instructions">Description</FakeLabel>
                    <Field
                      type="textarea"
                      id="node_instructions"
                      value={instructions}
                      onChange={e => setInstructions(e.target.value)}
                      placeholder="Describe the llm and its purpose..."
                      rows={6}
                    />
                  </div>
                  <div className="space-y-2">
                    <FakeLabel htmlFor="model-provider">Provider</FakeLabel>
                    <Field
                      options={modelsKeys}
                      type="select"
                      id="model-provider"
                      placeholder="Select any provider to proceed"
                      value={activeModelsKeys}
                      onChange={e => {
                        setActiveModelsKeys(e.target?.value);
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <FakeLabel htmlFor="node-model">Model</FakeLabel>
                    <Field
                      options={
                        models?.find(obj => Object.keys(obj)[0] === activeModelsKeys?.value)?.[activeModelsKeys?.value]
                      }
                      type="select"
                      id="node-model"
                      value={model}
                      onChange={e => setModel(e.target.value)}
                      placeholder="e.g., gpt-4, claude-3"
                    />
                  </div>
                  <div className="space-y-2">
                    <FakeLabel htmlFor="node_prompt">Prompt</FakeLabel>
                    <Field
                      type="textarea"
                      id="node_prompt"
                      value={prompt}
                      onChange={e => setPrompt(e.target.value)}
                      placeholder="System prompt..."
                      rows={6}
                    />
                  </div>
                </>
              )}

              {node.data.type === 'team' && (
                <>
                  <div className="space-y-2">
                    <FakeLabel htmlFor="team_id">Select Team</FakeLabel>
                    <Field
                      options={formatTeams(getAllTeamData?.data?.teams)}
                      type="select"
                      id="team_id"
                      value={teamId}
                      onChange={e => setTeamId(e.target.value)}
                      placeholder="Select any team"
                    />
                  </div>
                  <div className="space-y-2">
                    <FakeLabel htmlFor="node_instructions">Team Description</FakeLabel>
                    <Field
                      type="textarea"
                      id="node_instructions"
                      value={instructions}
                      onChange={e => setInstructions(e.target.value)}
                      placeholder="Describe the team and its purpose..."
                      rows={6}
                    />
                  </div>
                </>
              )}

              {node.data.type === 'condition' && (
                <div className="space-y-2">
                  <FakeLabel htmlFor="node-condition">Condition</FakeLabel>
                  <Field
                    type="textarea"
                    id="node-condition"
                    value={condition}
                    onChange={e => setCondition(e.target.value)}
                    placeholder="Define the condition logic..."
                    rows={6}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="panel-foot">
            <Button onClick={handleSave} className="w-full">
              Save Changes
            </Button>
          </div>
        </>
      )}
    </StyledNodeSettings>
  );
};
